import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, payload } from "./lib/api";
import { uploadMedia } from "./lib/upload-media";
import { markUnsaved } from "./lib/auto-update";
import type {
  ComponentNode,
  ComponentType,
  EditorViewport,
  MediaAsset,
  Page,
  Site,
  User,
} from "./types";
import universeMark from "../../Universe/favicon-512.png";
import "./editor.css";

type Panel =
  | "add"
  | "sections"
  | "navigator"
  | "pages"
  | "media"
  | "design";
type SaveState = "saved" | "dirty" | "saving" | "error";
interface PublishState {
  configured: boolean;
  publishable: boolean;
  reason: string | null;
  deployment: { status: string; url: string | null } | null;
}

const blankStyles = (): ComponentNode["styles"] => ({
  desktop: {},
  tablet: {},
  mobile: {},
});
const node = (
  type: ComponentType,
  content = "",
  props: ComponentNode["props"] = {},
): ComponentNode => ({
  id: crypto.randomUUID(),
  type,
  content,
  props,
  styles: blankStyles(),
  children: [],
});

function section(
  role = "section",
  children: ComponentNode[] = [],
): ComponentNode {
  return { ...node("section", "", { role }), children };
}

function preset(kind: string): ComponentNode {
  if (kind === "hero")
    return section("hero", [
      node("heading", "Scrivi qui il tuo messaggio", { level: 1 }),
      node(
        "text",
        "Racconta in modo chiaro cosa rende speciale la tua attività.",
      ),
      node("button", "Scopri di più", { href: "#" }),
    ]);
  if (kind === "image-text")
    return section("image-text", [
      node("image", "", { url: "", alt: "Descrizione immagine" }),
      node("heading", "Una storia da raccontare", { level: 2 }),
      node(
        "text",
        "Modifica questo testo direttamente nella pagina oppure dal pannello laterale.",
      ),
    ]);
  if (kind === "cta")
    return section("cta", [
      node("heading", "Pronto a iniziare?", { level: 2 }),
      node("text", "Aggiungi qui una frase breve e concreta."),
      node("button", "Contattaci", { href: "#contatti" }),
    ]);
  return section("content", [
    node("heading", "Titolo della sezione", { level: 2 }),
    node("text", "Clicca sul testo per modificarlo."),
  ]);
}

// Pagine registrate senza struttura (es. siti statici nell'editor protetto) hanno un contenuto vuoto "[]": le trattiamo come pagina vuota invece di andare in errore.
function normalizeTree(value: unknown): ComponentNode {
  const tree = value as ComponentNode | null;
  if (tree && typeof tree === "object" && !Array.isArray(tree) && Array.isArray(tree.children)) return tree;
  return { id: "root", type: "page", props: {}, styles: { desktop: {}, tablet: {}, mobile: {} }, children: [] };
}

function findNode(root: ComponentNode, id: string): ComponentNode | null {
  if (root.id === id) return root;
  for (const child of root.children) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return null;
}

function mapNode(
  root: ComponentNode,
  id: string,
  transform: (value: ComponentNode) => ComponentNode,
): ComponentNode {
  if (root.id === id) return transform(root);
  return {
    ...root,
    children: root.children.map((child) => mapNode(child, id, transform)),
  };
}

function removeNode(root: ComponentNode, id: string): ComponentNode {
  return {
    ...root,
    children: root.children
      .filter((child) => child.id !== id)
      .map((child) => removeNode(child, id)),
  };
}

function cloneNode(value: ComponentNode): ComponentNode {
  return {
    ...value,
    id: crypto.randomUUID(),
    styles: {
      desktop: { ...value.styles.desktop },
      tablet: { ...value.styles.tablet },
      mobile: { ...value.styles.mobile },
    },
    children: value.children.map(cloneNode),
  };
}

function parentId(root: ComponentNode, id: string): string | null {
  for (const child of root.children) {
    if (child.id === id) return root.id;
    const nested = parentId(child, id);
    if (nested) return nested;
  }
  return null;
}

function EditorIcon({ name }: { name: string }) {
  const symbols: Record<string, string> = {
    add: "+",
    sections: "▤",
    navigator: "☷",
    pages: "▱",
    media: "▧",
    design: "◐",
    undo: "↶",
    redo: "↷",
    desktop: "▰",
    tablet: "▯",
    mobile: "▯",
    preview: "◉",
    trash: "×",
    duplicate: "⧉",
    up: "↑",
    down: "↓",
  };
  return <span aria-hidden="true">{symbols[name] || "·"}</span>;
}

function nodeName(value: ComponentNode): string {
  if (value.type === "section") {
    if (value.props.role === "hero") return "Hero";
    if (value.props.role === "cta") return "Call to action";
    if (value.props.role === "image-text") return "Immagine e testo";
    return "Sezione";
  }
  if (value.type === "heading") return value.content || "Titolo";
  if (value.type === "text") return value.content || "Testo";
  if (value.type === "button") return value.content || "Pulsante";
  if (value.type === "image") return value.props.alt || "Immagine";
  if (value.type === "container") return "Contenitore";
  if (value.type === "divider") return "Linea";
  if (value.type === "spacer") return "Spazio";
  return "Pagina";
}

function NavigatorNode({
  value,
  selectedId,
  select,
  depth = 0,
}: {
  value: ComponentNode;
  selectedId: string;
  select: (id: string) => void;
  depth?: number;
}) {
  return (
    <li>
      <button
        className={selectedId === value.id ? "active" : ""}
        style={{ paddingLeft: `${10 + depth * 13}px` }}
        onClick={() => select(value.id)}
        title={nodeName(value)}
      >
        <EditorIcon name={value.type === "section" ? "sections" : value.type} />
        <span>{nodeName(value)}</span>
      </button>
      {value.children.length > 0 && (
        <ul>
          {value.children.map((child) => (
            <NavigatorNode
              key={child.id}
              value={child}
              selectedId={selectedId}
              select={select}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function styleFor(
  value: ComponentNode,
  viewport: EditorViewport,
): React.CSSProperties {
  const merged =
    viewport === "desktop"
      ? value.styles.desktop
      : { ...value.styles.desktop, ...value.styles[viewport] };
  return merged as React.CSSProperties;
}

function CanvasNode({
  value,
  viewport,
  selectedId,
  select,
  editText,
  dragSection,
  dropSection,
  canStructure,
  canEdit,
}: {
  value: ComponentNode;
  viewport: EditorViewport;
  selectedId: string;
  select: (id: string) => void;
  editText: (id: string, content: string) => void;
  dragSection: (id: string) => void;
  dropSection: (id: string) => void;
  canStructure: boolean;
  canEdit: boolean;
}) {
  const selected = selectedId === value.id;
  const common = {
    id: `ve-node-${value.id}`,
    style: styleFor(value, viewport),
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();
      select(value.id);
    },
  };
  if (value.type === "section")
    return (
      <section
        {...common}
        className={`ve-section role-${value.props.role || "content"} ${selected ? "is-selected" : ""}`}
        draggable={canStructure}
        onDragStart={() => dragSection(value.id)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          dropSection(value.id);
        }}
      >
        <span className="ve-section-label">
          {value.props.role === "hero"
            ? "Hero"
            : value.props.role === "cta"
              ? "Call to action"
              : value.props.role === "image-text"
                ? "Immagine + testo"
                : "Sezione"}{" "}
          · {canStructure ? "trascina per spostare" : "struttura protetta"}
        </span>
        <div className="ve-section-inner">
          {value.children.map((child) => (
            <CanvasNode
              key={child.id}
              value={child}
              viewport={viewport}
              selectedId={selectedId}
              select={select}
              editText={editText}
              dragSection={dragSection}
              dropSection={dropSection}
              canStructure={canStructure}
              canEdit={canEdit}
            />
          ))}
        </div>
      </section>
    );
  if (value.type === "container")
    return (
      <div
        {...common}
        className={`ve-container ${selected ? "is-selected" : ""}`}
      >
        {value.children.map((child) => (
          <CanvasNode
            key={child.id}
            value={child}
            viewport={viewport}
            selectedId={selectedId}
            select={select}
            editText={editText}
            dragSection={dragSection}
            dropSection={dropSection}
              canStructure={canStructure}
              canEdit={canEdit}
          />
        ))}
      </div>
    );
  const className = `ve-element ve-${value.type} ${selected ? "is-selected" : ""}`;
  if (value.type === "heading") {
    const headingProps = {
      ...common,
      className,
      contentEditable: canEdit,
      suppressContentEditableWarning: true,
      onBlur: (event: React.FocusEvent<HTMLHeadingElement>) => {
        if (canEdit)
          editText(value.id, event.currentTarget.textContent || "");
      },
    };
    if (value.props.level === 1)
      return <h1 {...headingProps}>{value.content}</h1>;
    if (value.props.level === 3)
      return <h3 {...headingProps}>{value.content}</h3>;
    return <h2 {...headingProps}>{value.content}</h2>;
  }
  if (value.type === "text")
    return (
      <p
        {...common}
        className={className}
        contentEditable={canEdit}
        suppressContentEditableWarning
        onBlur={(event) =>
          canEdit && editText(value.id, event.currentTarget.textContent || "")
        }
      >
        {value.content}
      </p>
    );
  if (value.type === "button")
    return (
      <a
        {...common}
        className={className}
        href={value.props.href || "#"}
        onClick={(event) => {
          event.preventDefault();
          common.onClick(event);
        }}
        contentEditable={canEdit}
        suppressContentEditableWarning
        onBlur={(event) =>
          canEdit && editText(value.id, event.currentTarget.textContent || "")
        }
      >
        {value.content || "Pulsante"}
      </a>
    );
  if (value.type === "image")
    return (
      <div {...common} className={className}>
        {value.props.url ? (
          <img
            src={value.props.url}
            alt={value.props.alt || ""}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="ve-image-empty">
            <span>＋</span>
            <strong>Aggiungi un’immagine</strong>
            <small>Seleziona l’elemento e inserisci l’URL</small>
          </div>
        )}
      </div>
    );
  if (value.type === "divider")
    return (
      <div {...common} className={className}>
        <hr />
      </div>
    );
  return <div {...common} className={className} />;
}

function PropertyPanel({
  selected,
  viewport,
  update,
  remove,
  duplicate,
  move,
  advanced,
  canEdit,
  openMedia,
  close,
}: {
  selected: ComponentNode | null;
  viewport: EditorViewport;
  update: (transform: (value: ComponentNode) => ComponentNode) => void;
  remove: () => void;
  duplicate: () => void;
  move: (delta: number) => void;
  advanced: boolean;
  canEdit: boolean;
  openMedia: () => void;
  close: () => void;
}) {
  if (!selected || selected.type === "page")
    return (
      <aside className="ve-properties">
        <div className="ve-panel-empty">
          <span>↖</span>
          <h3>Seleziona un elemento</h3>
          <p>
            Clicca su testo, immagine, pulsante o sezione per modificarne
            contenuto e design.
          </p>
        </div>
      </aside>
    );
  const styles = selected.styles[viewport];
  const setStyle = (key: string, value: string) =>
    update((current) => ({
      ...current,
      styles: {
        ...current.styles,
        [viewport]: { ...current.styles[viewport], [key]: value },
      },
    }));
  const setProp = (
    key: keyof ComponentNode["props"],
    value: string | boolean,
  ) =>
    update((current) => ({
      ...current,
      props: { ...current.props, [key]: value },
    }));
  return (
    <aside className="ve-properties">
      <div className="ve-properties-head">
        <div>
          <small>Elemento selezionato</small>
          <h2>
            {selected.type === "section"
              ? "Sezione"
              : selected.type === "heading"
                ? "Titolo"
                : selected.type === "text"
                  ? "Testo"
                  : selected.type === "image"
                    ? "Immagine"
                    : selected.type === "button"
                      ? "Pulsante"
                      : selected.type}
          </h2>
        </div>
        <div>
          {advanced && (
            <>
              <button
                onClick={() => move(-1)}
                title="Sposta su"
                aria-label="Sposta elemento verso l’alto"
              >
                <EditorIcon name="up" />
              </button>
              <button
                onClick={() => move(1)}
                title="Sposta giù"
                aria-label="Sposta elemento verso il basso"
              >
                <EditorIcon name="down" />
              </button>
            </>
          )}
          <button
            onClick={close}
            title="Chiudi proprietà"
            aria-label="Chiudi proprietà"
          >
            ×
          </button>
        </div>
      </div>
      {!advanced && selected.type === "section" && (
        <div className="ve-locked-note">
          <strong>Struttura protetta da Universe</strong>
          <p>
            Puoi modificare testi e immagini contenuti nella sezione senza
            alterarne layout o animazioni.
          </p>
        </div>
      )}
      {!canEdit && selected.type !== "section" && (
        <div className="ve-locked-note">
          <strong>Modalità sola lettura</strong>
          <p>Puoi consultare il sito, ma questo accesso non può modificarlo.</p>
        </div>
      )}
      {(selected.type === "heading" ||
        selected.type === "text" ||
        selected.type === "button") && (
        <fieldset disabled={!canEdit}>
          <legend>Contenuto</legend>
          <label>
            Testo
            <textarea
              value={selected.content || ""}
              onChange={(event) =>
                update((current) => ({
                  ...current,
                  content: event.target.value,
                }))
              }
            />
          </label>
          {selected.type === "button" && (
            <label>
              Collegamento
              <input
                value={selected.props.href || ""}
                onChange={(event) => setProp("href", event.target.value)}
                placeholder="/contatti oppure https://…"
              />
            </label>
          )}
        </fieldset>
      )}
      {selected.type === "image" && (
        <fieldset disabled={!canEdit}>
          <legend>Immagine</legend>
          <button className="ve-media-open" onClick={openMedia}>
            Scegli o carica immagine
          </button>
          {advanced && (
            <label>
              URL immagine
              <input
                type="url"
                value={selected.props.url || ""}
                onChange={(event) => setProp("url", event.target.value)}
                placeholder="https://…"
              />
            </label>
          )}
          <label>
            Testo alternativo
            <input
              value={selected.props.alt || ""}
              onChange={(event) => setProp("alt", event.target.value)}
            />
          </label>
        </fieldset>
      )}
      {advanced && (
        <fieldset>
          <legend>Design · {viewport}</legend>
          <div className="ve-field-pair">
            <label>
              Colore testo
              <input
                type="color"
                value={String(styles.color || "#14151a")}
                onChange={(event) => setStyle("color", event.target.value)}
              />
            </label>
            <label>
              Sfondo
              <input
                type="color"
                value={String(
                  styles.backgroundColor ||
                    (selected.type === "section" ? "#ffffff" : "#4f46e5"),
                )}
                onChange={(event) =>
                  setStyle("backgroundColor", event.target.value)
                }
              />
            </label>
          </div>
          {(selected.type === "heading" ||
            selected.type === "text" ||
            selected.type === "button") && (
            <>
              <label>
                Dimensione testo
                <input
                  type="range"
                  min="12"
                  max="96"
                  value={parseInt(
                    String(
                      styles.fontSize ||
                        (selected.type === "heading" ? 48 : 18),
                    ),
                    10,
                  )}
                  onChange={(event) =>
                    setStyle("fontSize", `${event.target.value}px`)
                  }
                />
                <output>{String(styles.fontSize || "Automatica")}</output>
              </label>
              <label>
                Allineamento
                <select
                  value={String(styles.textAlign || "left")}
                  onChange={(event) =>
                    setStyle("textAlign", event.target.value)
                  }
                >
                  <option value="left">Sinistra</option>
                  <option value="center">Centro</option>
                  <option value="right">Destra</option>
                </select>
              </label>
            </>
          )}
          <div className="ve-field-pair">
            <label>
              Spazio sopra
              <input
                type="number"
                min="0"
                max="240"
                value={parseInt(String(styles.paddingTop || 0), 10)}
                onChange={(event) =>
                  setStyle("paddingTop", `${event.target.value}px`)
                }
              />
            </label>
            <label>
              Spazio sotto
              <input
                type="number"
                min="0"
                max="240"
                value={parseInt(String(styles.paddingBottom || 0), 10)}
                onChange={(event) =>
                  setStyle("paddingBottom", `${event.target.value}px`)
                }
              />
            </label>
          </div>
          <label>
            Arrotondamento
            <input
              type="range"
              min="0"
              max="80"
              value={parseInt(String(styles.borderRadius || 0), 10)}
              onChange={(event) =>
                setStyle("borderRadius", `${event.target.value}px`)
              }
            />
          </label>
        </fieldset>
      )}
      {advanced && (
        <div className="ve-element-actions">
          <button onClick={duplicate}>
            <EditorIcon name="duplicate" />
            Duplica
          </button>
          <button className="danger" onClick={remove}>
            <EditorIcon name="trash" />
            Elimina
          </button>
        </div>
      )}
    </aside>
  );
}

export default function VisualEditor({
  user,
  canEdit,
}: {
  user: User;
  canEdit: boolean;
}) {
  const { siteId = "", pageId = "" } = useParams();
  const navigate = useNavigate();
  const advanced = user.role === "admin";
  const [site, setSite] = useState<Site | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [page, setPage] = useState<Page | null>(null);
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaSearch, setMediaSearch] = useState("");
  const [mediaEditing, setMediaEditing] = useState<{
    id: string;
    filename: string;
    altText: string;
  } | null>(null);
  const [tree, setTree] = useState<ComponentNode | null>(null);
  const [selectedId, setSelectedId] = useState("root");
  const [panel, setPanel] = useState<Panel>(advanced ? "add" : "navigator");
  const [viewport, setViewport] = useState<EditorViewport>("desktop");
  const [saveState, setSaveState] = useState<SaveState>("saved");
  // Pagina modificata ma non ancora salvata (o salvataggio fallito): niente aggiornamento automatico.
  useEffect(() => {
    markUnsaved("visual-editor", saveState !== "saved");
    return () => markUnsaved("visual-editor", false);
  }, [saveState]);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  const [publishState, setPublishState] = useState<PublishState | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");
  const [past, setPast] = useState<ComponentNode[]>([]);
  const [future, setFuture] = useState<ComponentNode[]>([]);
  const dragged = useRef("");
  const loaded = useRef(false);
  const revision = useRef(0);
  const activePageId = useRef("");
  const saveQueue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    loaded.current = false;
    activePageId.current = "";
    Promise.all([
      api<{ site: Site }>(`/api/sites/${siteId}`),
      api<{ pages: Page[] }>(`/api/sites/${siteId}/pages`),
    ])
      .then(([siteResult, pageResult]) => {
        const current = pageResult.pages.find((item) => item.id === pageId);
        if (!current) throw new Error("Pagina non trovata");
        setSite(siteResult.site);
        setPages(pageResult.pages);
        setPage(current);
        setTree(normalizeTree(current.componentTree));
        setSelectedId("root");
        setPast([]);
        setFuture([]);
        setSaveState("saved");
        revision.current = 0;
        activePageId.current = current.id;
        loaded.current = true;
      })
      .catch((cause) => setError(cause.message));
  }, [pageId, siteId]);
  useEffect(() => {
    api<PublishState>(`/api/sites/${siteId}/publish`)
      .then(setPublishState)
      .catch((cause) => setError(cause.message));
  }, [siteId]);
  useEffect(() => {
    if (panel === "media")
      api<{ media: MediaAsset[] }>(`/api/sites/${siteId}/media`)
        .then((result) => setMedia(result.media))
        .catch((cause) => setError(cause.message));
  }, [panel, siteId]);

  const commit = useCallback((next: ComponentNode) => {
    if (!canEdit) return;
    revision.current += 1;
    setTree((current) => {
      if (current) setPast((items) => [...items.slice(-49), current]);
      return next;
    });
    setFuture([]);
    setSaveState("dirty");
  }, [canEdit]);
  const updateSelected = useCallback(
    (transform: (value: ComponentNode) => ComponentNode) => {
      if (tree) commit(mapNode(tree, selectedId, transform));
    },
    [commit, selectedId, tree],
  );
  const undo = useCallback(() => {
    if (!canEdit || !tree || !past.length) return;
    const previous = past[past.length - 1];
    setPast((items) => items.slice(0, -1));
    setFuture((items) => [tree, ...items]);
    setTree(previous);
    revision.current += 1;
    setSaveState("dirty");
  }, [canEdit, past, tree]);
  const redo = useCallback(() => {
    if (!canEdit || !tree || !future.length) return;
    const next = future[0];
    setFuture((items) => items.slice(1));
    setPast((items) => [...items, tree]);
    setTree(next);
    revision.current += 1;
    setSaveState("dirty");
  }, [canEdit, future, tree]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z")
        return;
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [redo, undo]);
  useEffect(() => {
    if (
      !canEdit ||
      !tree ||
      !page ||
      !loaded.current ||
      saveState !== "dirty"
    )
      return;
    const timeout = window.setTimeout(() => {
      const snapshot = tree;
      const snapshotRevision = revision.current;
      const snapshotPageId = page.id;
      setSaveState("saving");
      const execute = async () => {
        try {
          const result = await api<{ page: Page }>(
            `/api/pages/${snapshotPageId}`,
            payload("PATCH", { componentTree: snapshot }),
          );
          if (
            activePageId.current === snapshotPageId &&
            revision.current === snapshotRevision
          ) {
            setPage(result.page);
            setSaveState("saved");
          }
        } catch (cause) {
          if (activePageId.current === snapshotPageId) {
            setSaveState("error");
            setError(
              cause instanceof Error
                ? cause.message
                : "Salvataggio non riuscito",
            );
          }
        }
      };
      saveQueue.current = saveQueue.current.then(execute, execute);
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [canEdit, page, saveState, tree]);

  const selected = useMemo(
    () => (tree ? findNode(tree, selectedId) : null),
    [selectedId, tree],
  );
  const selectFromNavigator = useCallback((id: string) => {
    setSelectedId(id);
    window.requestAnimationFrame(() => {
      document.getElementById(`ve-node-${id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, []);
  function addElement(type: ComponentType) {
    if (!tree) return;
    const templates: Record<string, ComponentNode> = {
      heading: node("heading", "Nuovo titolo", { level: 2 }),
      text: node("text", "Scrivi qui il tuo testo."),
      image: node("image", "", { url: "", alt: "" }),
      button: node("button", "Nuovo pulsante", { href: "#" }),
      divider: node("divider"),
      spacer: node("spacer"),
    };
    const fresh = templates[type] || node(type);
    const target =
      selected?.type === "section" || selected?.type === "container"
        ? selected.id
        : parentId(tree, selectedId) || tree.children[0]?.id || "root";
    commit(
      mapNode(tree, target, (current) => ({
        ...current,
        children: [...current.children, fresh],
      })),
    );
    setSelectedId(fresh.id);
  }
  function addSection(kind: string) {
    if (!tree) return;
    const fresh = preset(kind);
    commit({ ...tree, children: [...tree.children, fresh] });
    setSelectedId(fresh.id);
  }
  function removeSelected() {
    if (!tree || selectedId === "root") return;
    commit(removeNode(tree, selectedId));
    setSelectedId("root");
  }
  function duplicateSelected() {
    if (!tree || !selected || selectedId === "root") return;
    const parent = parentId(tree, selectedId);
    if (!parent) return;
    const copy = cloneNode(selected);
    commit(
      mapNode(tree, parent, (current) => {
        const index = current.children.findIndex(
          (item) => item.id === selectedId,
        );
        const children = [...current.children];
        children.splice(index + 1, 0, copy);
        return { ...current, children };
      }),
    );
    setSelectedId(copy.id);
  }
  function moveSelected(delta: number) {
    if (!tree || selectedId === "root") return;
    const parent = parentId(tree, selectedId);
    if (!parent) return;
    commit(
      mapNode(tree, parent, (current) => {
        const children = [...current.children];
        const index = children.findIndex((item) => item.id === selectedId);
        const next = Math.max(0, Math.min(children.length - 1, index + delta));
        if (index === next) return current;
        [children[index], children[next]] = [children[next], children[index]];
        return { ...current, children };
      }),
    );
  }
  function dropSection(targetId: string) {
    if (!tree || !dragged.current || dragged.current === targetId) return;
    const children = [...tree.children];
    const from = children.findIndex((item) => item.id === dragged.current);
    const to = children.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = children.splice(from, 1);
    children.splice(to, 0, moved);
    dragged.current = "";
    commit({ ...tree, children });
  }
  async function uploadImage(event: React.ChangeEvent<HTMLInputElement>) {
    if (!canEdit) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setMediaBusy(true);
    setError("");
    try {
      const result = { media: await uploadMedia<MediaAsset>(siteId, file) };
      setMedia((items) => [result.media, ...items.filter((item) => item.id !== result.media.id)]);
      if (selected?.type === "image")
        updateSelected((current) => ({
          ...current,
          props: {
            ...current.props,
            url: result.media.url,
            alt: current.props.alt || result.media.altText || "",
          },
        }));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Caricamento non riuscito",
      );
    } finally {
      setMediaBusy(false);
      event.target.value = "";
    }
  }
  function chooseImage(item: MediaAsset) {
    if (!canEdit) return;
    if (selected?.type !== "image") {
      setError("Seleziona prima un’immagine nella pagina");
      return;
    }
    updateSelected((current) => ({
      ...current,
      props: {
        ...current.props,
        url: item.url,
        alt: current.props.alt || item.altText || "",
      },
    }));
  }
  async function saveMediaMetadata(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canEdit || !mediaEditing) return;
    setMediaBusy(true);
    setError("");
    try {
      const result = await api<{ media: MediaAsset }>(
        `/api/media/${mediaEditing.id}`,
        payload("PATCH", {
          filename: mediaEditing.filename,
          altText: mediaEditing.altText,
        }),
      );
      setMedia((items) =>
        items.map((current) =>
          current.id === mediaEditing.id ? result.media : current,
        ),
      );
      setMediaEditing(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Modifica non riuscita",
      );
    } finally {
      setMediaBusy(false);
    }
  }
  async function removeMedia(item: MediaAsset) {
    if (!canEdit) return;
    if (!window.confirm(`Eliminare la foto “${item.filename}”?`)) return;
    setMediaBusy(true);
    setError("");
    try {
      await api(`/api/media/${item.id}`, payload("DELETE"));
      setMedia((items) => items.filter((current) => current.id !== item.id));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Eliminazione non riuscita",
      );
    } finally {
      setMediaBusy(false);
    }
  }
  const filteredMedia = useMemo(() => {
    const query = mediaSearch.trim().toLocaleLowerCase("it");
    if (!query) return media;
    return media.filter((item) =>
      `${item.filename} ${item.altText || ""}`
        .toLocaleLowerCase("it")
        .includes(query),
    );
  }, [media, mediaSearch]);
  async function publish() {
    if (
      saveState !== "saved" ||
      !window.confirm("Pubblicare ora tutte le pagine del sito su Cloudflare?")
    )
      return;
    setPublishBusy(true);
    setPublishMessage("");
    setError("");
    try {
      const result = await api<{
        deployment: { url: string; verifiedHttpStatus: number };
      }>(`/api/sites/${siteId}/publish`, payload("POST"));
      setPublishMessage(
        `Online e verificato HTTP ${result.deployment.verifiedHttpStatus}`,
      );
      setPublishState((current) =>
        current
          ? {
              ...current,
              deployment: { status: "success", url: result.deployment.url },
            }
          : current,
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Pubblicazione non riuscita",
      );
    } finally {
      setPublishBusy(false);
    }
  }

  if (error && !tree)
    return (
      <main className="ve-fatal">
        <img src={universeMark} alt="Universe" />
        <h1>Editor non disponibile</h1>
        <p>{error}</p>
        <button onClick={() => navigate(`/sites/${siteId}`)}>
          Torna al progetto
        </button>
      </main>
    );
  if (!tree || !page || !site)
    return (
      <main className="ve-loading">
        <img src={universeMark} alt="Universe" />
        <p>Preparazione editor visuale…</p>
      </main>
    );
  const saveLabel =
    saveState === "saving"
      ? "Salvataggio…"
      : saveState === "dirty"
        ? "Modifiche non salvate"
        : saveState === "error"
          ? "Errore salvataggio"
          : "Salvato";
  return (
    <main
      className={`visual-editor ${preview ? "preview-mode" : ""} ${selectedId !== "root" ? "properties-open" : ""}`}
    >
      <header className="ve-topbar">
        <button
          className="ve-brand"
          onClick={() => navigate(`/sites/${siteId}`)}
          aria-label="Torna alle pagine del sito"
        >
          <img src={universeMark} alt="" />
          <strong>UNIVERSE</strong>
          <span>{advanced ? "Editor" : "Contenuti"}</span>
        </button>
        <div className="ve-page-title">
          <strong>{page.title}</strong>
          <small>{site.name}</small>
        </div>
        <div className="ve-history">
          <button
            onClick={undo}
            disabled={!canEdit || !past.length}
            title="Annulla"
            aria-label="Annulla ultima modifica"
          >
            <EditorIcon name="undo" />
          </button>
          <button
            onClick={redo}
            disabled={!canEdit || !future.length}
            title="Ripristina"
            aria-label="Ripristina modifica annullata"
          >
            <EditorIcon name="redo" />
          </button>
          <span className={saveState}>{saveLabel}</span>
        </div>
        <div className="ve-viewports">
          {(["desktop", "tablet", "mobile"] as EditorViewport[]).map((item) => (
            <button
              key={item}
              className={viewport === item ? "active" : ""}
              onClick={() => setViewport(item)}
              title={item}
              aria-label={`Anteprima ${item}`}
              aria-pressed={viewport === item}
            >
              <EditorIcon name={item} />
            </button>
          ))}
        </div>
        <div className="ve-top-actions">
          {site?.modules?.includes("restaurant_menu") && (
            <button type="button" onClick={() => navigate(`/sites/${siteId}?section=menu`)}>
              Modifica il menu
            </button>
          )}
          <button onClick={() => setPreview((value) => !value)}>
            <EditorIcon name="preview" />
            {preview ? "Torna all’editor" : "Anteprima"}
          </button>
          {canEdit &&
            (advanced || publishState?.configured) &&
            publishState?.publishable && (
              <button
                className="publish"
                disabled={
                  publishBusy ||
                  saveState !== "saved" ||
                  !publishState?.configured ||
                  !publishState.publishable
                }
                title={
                  publishState?.reason ||
                  (!publishState?.configured
                    ? "Configura il token Cloudflare nel Worker"
                    : saveState !== "saved"
                      ? "Attendi il salvataggio"
                      : "Pubblica su Cloudflare")
                }
                onClick={publish}
              >
                {publishBusy ? "Pubblicazione…" : "Pubblica"}
              </button>
            )}
        </div>
      </header>
      {(error || publishMessage) && (
        <div
          className={`ve-toast ${error ? "error" : "success"}`}
          role={error ? "alert" : "status"}
        >
          <span>{error || publishMessage}</span>
          <button
            onClick={() => {
              setError("");
              setPublishMessage("");
            }}
            aria-label="Chiudi messaggio"
          >
            ×
          </button>
        </div>
      )}
      {(publishMessage || (advanced && publishState?.reason)) && (
        <div className="ve-publish-note">
          {publishMessage || publishState?.reason}
        </div>
      )}
      <div className="ve-body">
        <aside className="ve-tools">
          {(
            (advanced
              ? [
                  { key: "add", label: "Aggiungi" },
                  { key: "sections", label: "Sezioni" },
                  { key: "navigator", label: "Livelli" },
                  { key: "pages", label: "Pagine" },
                  { key: "media", label: "Media" },
                  { key: "design", label: "Design" },
                ]
              : [
                  { key: "navigator", label: "Contenuti" },
                  { key: "pages", label: "Pagine" },
                  { key: "media", label: "Foto" },
                ]) as { key: Panel; label: string }[]
          ).map((item) => (
            <button
              key={item.key}
              className={panel === item.key ? "active" : ""}
              onClick={() => setPanel(item.key)}
            >
              <EditorIcon name={item.key} />
              <small>{item.label}</small>
            </button>
          ))}
        </aside>
        <aside className="ve-library">
          <div className="ve-library-head">
            <small>Strumenti</small>
            <h2>
              {panel === "add"
                ? "Aggiungi elementi"
                : panel === "sections"
                  ? "Sezioni pronte"
                  : panel === "navigator"
                    ? "Contenuti pagina"
                  : panel === "pages"
                    ? "Pagine del sito"
                    : panel === "media"
                      ? "Foto del sito"
                      : "Design pagina"}
            </h2>
          </div>
          {panel === "add" && (
            <div className="ve-add-grid">
              {(
                [
                  ["heading", "Titolo", "T"],
                  ["text", "Testo", "¶"],
                  ["image", "Immagine", "▧"],
                  ["button", "Pulsante", "→"],
                  ["divider", "Linea", "—"],
                  ["spacer", "Spazio", "↕"],
                ] as [ComponentType, string, string][]
              ).map(([type, label, symbol]) => (
                <button key={type} onClick={() => addElement(type)}>
                  <span>{symbol}</span>
                  <strong>{label}</strong>
                </button>
              ))}
            </div>
          )}
          {panel === "sections" && (
            <div className="ve-presets">
              <button onClick={() => addSection("hero")}>
                <span className="preset-hero" />
                <strong>Hero con azione</strong>
                <small>Titolo, testo e pulsante</small>
              </button>
              <button onClick={() => addSection("image-text")}>
                <span className="preset-split" />
                <strong>Immagine e testo</strong>
                <small>Layout editoriale</small>
              </button>
              <button onClick={() => addSection("content")}>
                <span className="preset-content" />
                <strong>Contenuto</strong>
                <small>Titolo e paragrafo</small>
              </button>
              <button onClick={() => addSection("cta")}>
                <span className="preset-cta" />
                <strong>Call to action</strong>
                <small>Invito finale</small>
              </button>
            </div>
          )}
          {panel === "navigator" && (
            <nav className="ve-navigator" aria-label="Struttura della pagina">
              <ul>
                {tree.children.map((child) => (
                  <NavigatorNode
                    key={child.id}
                    value={child}
                    selectedId={selectedId}
                    select={selectFromNavigator}
                  />
                ))}
              </ul>
            </nav>
          )}
          {panel === "pages" && (
            <nav className="ve-page-list">
              {pages.map((item) => (
                <button
                  key={item.id}
                  className={item.id === page.id ? "active" : ""}
                  onClick={() => navigate(`/sites/${siteId}/editor/${item.id}`)}
                >
                  <span>{item.isHome ? "⌂" : "□"}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.isHome ? "/" : `/${item.slug}`}</small>
                  </div>
                </button>
              ))}
            </nav>
          )}
          {panel === "media" && (
            <div className="ve-media-library">
              {canEdit ? <label className="ve-upload">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  onChange={uploadImage}
                  disabled={mediaBusy || !canEdit}
                />
                <span>{mediaBusy ? "Caricamento…" : "＋ Carica una foto"}</span>
                <small>JPG, PNG, WebP o AVIF · max 5 MB</small>
              </label> : <div className="ve-locked-note"><strong>Foto in sola lettura</strong><p>Questo accesso può consultare la libreria ma non modificarla.</p></div>}
              {media.length > 0 && (
                <label className="ve-media-search">
                  <span>Cerca nelle foto</span>
                  <input
                    type="search"
                    value={mediaSearch}
                    onChange={(event) => setMediaSearch(event.target.value)}
                    placeholder="Nome o testo alternativo"
                  />
                </label>
              )}
              {filteredMedia.length ? (
                <div className="ve-media-grid">
                  {filteredMedia.map((item) => (
                    <article
                      key={item.id}
                      className={mediaEditing?.id === item.id ? "editing" : ""}
                    >
                      <button
                        className="ve-media-thumb"
                        onClick={() => chooseImage(item)}
                        title={`Usa ${item.filename}`}
                        disabled={!canEdit}
                      >
                        <img src={item.url} alt={item.altText || item.filename} />
                        <small>{item.filename}</small>
                      </button>
                      {canEdit && <div>
                        <button
                          onClick={() =>
                            setMediaEditing({
                              id: item.id,
                              filename: item.filename,
                              altText: item.altText || "",
                            })
                          }
                          disabled={mediaBusy}
                          aria-label={`Modifica nome e testo alternativo di ${item.filename}`}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => removeMedia(item)}
                          disabled={mediaBusy}
                          aria-label={`Elimina ${item.filename}`}
                        >
                          Elimina
                        </button>
                      </div>}
                      {mediaEditing?.id === item.id && (
                        <form
                          className="ve-media-edit"
                          onSubmit={saveMediaMetadata}
                        >
                          <label>
                            Nome della foto
                            <input
                              value={mediaEditing.filename}
                              onChange={(event) =>
                                setMediaEditing({
                                  ...mediaEditing,
                                  filename: event.target.value,
                                })
                              }
                              maxLength={120}
                              required
                            />
                          </label>
                          <label>
                            Testo alternativo
                            <textarea
                              value={mediaEditing.altText}
                              onChange={(event) =>
                                setMediaEditing({
                                  ...mediaEditing,
                                  altText: event.target.value,
                                })
                              }
                              maxLength={300}
                              placeholder="Descrivi ciò che si vede"
                            />
                          </label>
                          <div>
                            <button
                              type="button"
                              onClick={() => setMediaEditing(null)}
                            >
                              Annulla
                            </button>
                            <button disabled={mediaBusy}>Salva</button>
                          </div>
                        </form>
                      )}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="ve-media-empty">
                  {media.length
                    ? "Nessuna foto corrisponde alla ricerca."
                    : "Le foto caricate appariranno qui."}
                </div>
              )}
            </div>
          )}
          {panel === "design" && (
            <div className="ve-design-help">
              <span>◐</span>
              <h3>Design contestuale</h3>
              <p>
                Seleziona una sezione o un elemento. Colori, spazi e tipografia
                appariranno nel pannello a destra e saranno salvati per il
                dispositivo scelto.
              </p>
            </div>
          )}
        </aside>
        <section className="ve-stage" onClick={() => setSelectedId("root")}>
          <div className={`ve-viewport viewport-${viewport}`}>
            <div className="ve-site-nav">
              <strong>{site.name}</strong>
              <span>
                {pages
                  .filter(
                    (item) => item.inNavigation && item.status !== "hidden",
                  )
                  .map((item) => item.title)
                  .join("   ")}
              </span>
              <span className="ve-menu-preview">Menu</span>
            </div>
            {tree.children.length ? (
              tree.children.map((child) => (
                <CanvasNode
                  key={child.id}
                  value={child}
                  viewport={viewport}
                  selectedId={selectedId}
                  select={setSelectedId}
                  editText={(id, content) => {
                    const current = findNode(tree, id);
                    const original = String(current?.content || "").toLowerCase();
                    const next = content.toLowerCase();
                    const signature = (value: string) =>
                      value.length <= 180 &&
                      (/realizzato da[^a-z0-9]{0,16}universe/.test(value) ||
                        /made by[^a-z0-9]{0,16}universe/.test(value) ||
                        /erstellt von[^a-z0-9]{0,16}universe/.test(value) ||
                        /r[eé]alis[eé] par[^a-z0-9]{0,16}universe/.test(value));
                    if (signature(original) || signature(next)) return;
                    if (current?.content !== content)
                      commit(
                        mapNode(tree, id, (value) => ({ ...value, content })),
                      );
                  }}
                  dragSection={(id) => {
                    if (advanced) dragged.current = id;
                  }}
                  dropSection={(id) => {
                    if (advanced) dropSection(id);
                  }}
                  canStructure={advanced}
                  canEdit={canEdit}
                />
              ))
            ) : advanced ? (
              <button
                className="ve-empty-canvas"
                onClick={(event) => {
                  event.stopPropagation();
                  addSection("hero");
                }}
              >
                <span>＋</span>
                <strong>Aggiungi la prima sezione</strong>
              </button>
            ) : (
              <div className="ve-empty-canvas">
                <strong>La pagina non contiene sezioni modificabili</strong>
              </div>
            )}
            <footer className="ve-site-footer">
              <strong>{site.name}</strong>
              <small>© {new Date().getFullYear()}</small>
            </footer>
          </div>
        </section>
        <PropertyPanel
          selected={selected}
          viewport={viewport}
          update={updateSelected}
          remove={removeSelected}
          duplicate={duplicateSelected}
          move={moveSelected}
          advanced={advanced}
          canEdit={canEdit}
          openMedia={() => setPanel("media")}
          close={() => setSelectedId("root")}
        />
      </div>
    </main>
  );
}
